import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Image,
  Text,
  SimpleGrid,
  Skeleton,
  Stack,
  Button,
  Flex,
  Heading,
  IconButton,
  Icon,
  Spacer,
} from '@chakra-ui/react';
import { PlusCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import ContentDrawer from '@/pages/contents/content-drawer';
import { LoginButton } from '@/components/ui/login-button';
import { baseApi } from '@/utils/api';
import useDebounce from '@/lib/hooks/use-debounce';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { Content, FieldOption } from '@/lib/types';
import Filters from './elements/filters';
import Search from '@/components/list-controls/search';
import useContentState from './elements/use-content-state';
import useContentData from './elements/use-content-data';

interface Props {
  referenceId: string;
  hasHeader?: boolean;
  hasActions?: boolean;
  contentRefs?: string[];
  onAddContent?(item: Content): void;
  onRemoveContent?(refId: string): void;
}

interface FilterConfig {
  label: string;
  id: string;
  key: string;
  options: FieldOption[];
}

const PublicContent = ({
  referenceId,
  hasHeader = false,
  hasActions = false,
  contentRefs,
  onAddContent,
  onRemoveContent,
}: Props) => {
  const [filterConfig, setFilterConfig] = useState<FilterConfig[]>([]);
  const [facetFields, setFacetFields] = useState<string[]>([]);
  const contentDrawerModal = useModalToggle();
  const [selectedContent, setSelectedContent] = useState<Content | undefined>();

  const {
    filters,
    setFilters,
    selectListFilters,
    setSelectListFilters,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    initialized,
    setInitialized,
    resetState,
  } = useContentState();

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const fetchFields = useCallback(async () => {
    if (!referenceId) return;

    try {
      const response = await baseApi.get(`public/content-type-fields/content-type/${referenceId}`);
      const fields = response.data.data || [];
      const config = [];
      const facetFields = [];

      for (const field of fields) {
        const selectItem = {
          label: field.field_name,
          id: field.field_id,
          key: `content.${field.referenceID}.value`,
          options: field.field_options,
        };
        config.push(selectItem);
        facetFields.push(selectItem.key);
      }

      return { config, facetFields };
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: 'Failed to fetch filters',
      });
      throw err;
    }
  }, [referenceId]);

  useEffect(() => {
    if (referenceId) {
      const loadFields = async () => {
        try {
          setInitialized(false);
          const result = await fetchFields();

          if (result) {
            resetState();
            setFilters([{ key: 'status', val: 'published' }]);
            setFilterConfig(result.config);
            setFacetFields(result.facetFields);
          }
        } catch (error) {
          console.error(error);
        } finally {
          setInitialized(true);
        }
      };

      loadFields();
    }
  }, [referenceId, fetchFields, resetState, setFilters, setInitialized]);

  const { contents, optionConfig, isLoading, totalPages, header } = useContentData({
    initialized,
    referenceId,
    filters,
    selectListFilters,
    facetFields,
    searchQuery: debouncedSearchQuery,
    pageNumber: page,
    hasHeader,
  });

  const handleFilterChange = useCallback(
    (newFilters: any) => {
      const formattedFilters = Object.entries(newFilters).map(([key, value]) => ({
        key: key,
        val: (value as string[]).join(','),
      }));
      setSelectListFilters(formattedFilters);
      setPage(1);
    },
    [setSelectListFilters, setPage],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setPage(1);
    },
    [setSearchQuery, setPage],
  );

  const handleResetFilters = useCallback(() => {
    setSelectListFilters([]);
    setPage(1);
  }, [setSelectListFilters, setPage]);

  async function handleView(item: Content) {
    try {
      const response = await baseApi.get(`public/content/${item.reference_id}`);
      const content = await response.data.data;
      if (!content) {
        return;
      }
      setSelectedContent(content);
      contentDrawerModal.open();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch content',
      });
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  return (
    <Stack gap={3}>
      <Stack gap={3}>
        {hasHeader && header && (
          <Flex justifyContent={'space-between'}>
            <Stack>
              <Heading size='3xl'>{header?.name}</Heading>
              <Text fontSize='sm' color='fg.muted'>
                {header?.description}
              </Text>
            </Stack>
            <LoginButton />
          </Flex>
        )}
        <Box w={'full'}>
          <Search
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onClearSearch={() => setSearchQuery('')}
          />
        </Box>
        {isLoading && (
          <SimpleGrid columns={{ base: 2, md: 2, lg: 3 }} gap={6}>
            <Skeleton variant='shine' height={'214px'} />
            <Skeleton variant='shine' height={'214px'} />
            <Skeleton variant='shine' height={'214px'} />
            <Skeleton variant='shine' height={'214px'} />
            <Skeleton variant='shine' height={'214px'} />
            <Skeleton variant='shine' height={'214px'} />
          </SimpleGrid>
        )}
      </Stack>
      {contents.length > 0 && !isLoading ? (
        <Stack gap={3}>
          <Box w={'full'}>
            <Filters
              filterConfig={filterConfig}
              optionConfig={optionConfig}
              filters={filters}
              setFilters={setFilters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
            />
          </Box>
          <SimpleGrid columns={{ base: 2, md: 2, lg: 3 }} gap={6}>
            {contents.map((content) => (
              <Card.Root
                key={content.id}
                _hover={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(content);
                }}
              >
                <Box display='block' alignItems='center'>
                  <Image
                    objectFit={'cover'}
                    w={'100%'}
                    h={'130px'}
                    src={content.thumbnail ?? '/no_media.png'}
                    borderRadius={'md'}
                    alt={content.name}
                  />
                  <Stack gap={1} p={3}>
                    <Flex>
                      <Box>
                        <Heading size={'sm'} fontWeight={'bold'}>
                          {content.name}
                        </Heading>
                        <Text color={'fg.muted'} fontSize={'xs'}>
                          {new Date(content.created_at).toLocaleDateString()}
                        </Text>
                      </Box>
                      <Spacer />
                      {hasActions && (
                        <Flex>
                          {contentRefs?.includes(content.reference_id) ? (
                            <IconButton
                              variant='ghost'
                              rounded='full'
                              size={'sm'}
                              aria-label={'Add a content'}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveContent?.(content.reference_id);
                              }}
                            >
                              <Icon as={MinusCircleIcon} w={6} h={6} />
                            </IconButton>
                          ) : (
                            <IconButton
                              variant='ghost'
                              rounded='full'
                              size={'sm'}
                              aria-label={'Add a content'}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddContent?.(content);
                              }}
                            >
                              <Icon as={PlusCircleIcon} w={6} h={6} />
                            </IconButton>
                          )}
                        </Flex>
                      )}
                    </Flex>
                  </Stack>
                </Box>
              </Card.Root>
            ))}
          </SimpleGrid>
          <Flex mt={8} mb={4} justifyContent={'space-between'}>
            <Button
              colorPalette='brand'
              size='sm'
              onClick={handlePreviousPage}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Text fontSize='sm' my={2}>
              Page {page} of {totalPages}
            </Text>
            <Button
              colorPalette='brand'
              size='sm'
              onClick={handleNextPage}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </Flex>
        </Stack>
      ) : (
        <Text fontSize='sm' color='fg.muted' textAlign='center'>
          No content available
        </Text>
      )}

      {selectedContent && <ContentDrawer {...contentDrawerModal.props} content={selectedContent} />}
    </Stack>
  );
};

export default PublicContent;
